import { useCallback, useEffect, useRef } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router';

import { BaseUrlRoute, ChatRoute, PersonsRoute, VerifyAdminRoute } from '@/config/router/routes';
import {
  APP_LANGUAGE_NAME,
  APP_LANGUAGE_VALUES,
  type AppLanguage,
} from '@/domain/models/appLanguage';
import { PersonMoodState } from '@/domain/models/personMoodModel';
import { formatVersion } from '@/domain/models/versionModel';
import { VerifyAdminType } from '@/presentation/pages/verifyAdmin/verifyAdminType';
import { appStateStore, useAppStateStore } from '@/presentation/stores/appStateStore';
import { valueOrNull } from '@/presentation/stores/asyncValue';
import { languageStore } from '@/presentation/stores/languageStore';
import {
  personMoodStore,
  usePersonMood,
  usePersonMoodSupported,
} from '@/presentation/stores/personMoodStore';
import { BackgroundWrapper } from '@/presentation/widgets/BackgroundWrapper';
import { LanguageLottie } from '@/presentation/widgets/LanguageLottie';
import { EasyLoading } from '@/presentation/widgets/loading/loadingOverlay';
import { PrimaryButton } from '@/presentation/widgets/PrimaryButton';
import { PrimaryIconButtonRounded } from '@/presentation/widgets/PrimaryIconButton';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import { SvgAsset } from '@/presentation/widgets/SvgAsset';
import { stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureOp } from '@/utils/exceptions/appFailure';
import { wv } from '@/utils/screenUtil';

import styles from './LanguagePage.module.css';

/**
 * Перенос `presentation/pages/language/language_page.dart`.
 *
 * Установка APK, киоск-режим и прочая Android-часть на этот экран не влияли:
 * страница переехала целиком, кроме обёртки `Visibility.maintain` — почему,
 * сказано в [LanguagePage].
 */

/** `_versionTapsToDiagnostics = 5` */
const VERSION_TAPS_TO_DIAGNOSTICS = 5;

/** `_versionTapWindow = Duration(seconds: 3)` */
const VERSION_TAP_WINDOW_MS = 3000;

/**
 * `mainAxisExtent: 88.w` у ячейки сетки.
 *
 * Своя высота `PrimaryButton` — сырые 88 логических пикселей, но жёсткое
 * ограничение ячейки её перебивало, поэтому кнопке передаётся `height`.
 */
const GRID_ITEM_EXTENT = 88;

/** `Spacer()` в ряду действий подвала. */
const SPACER_STYLE = { flex: 1 } as const;

/**
 * Ветка `result == true`: есть аватар — в чат, нет — к списку аватаров.
 *
 * Отличие от Dart: там на этом месте стоял PIN
 * (`VerifyAdminRoute(changePerson)`), потому что планшет могли только что
 * подключить и выбор аватара считался действием администратора. Киоск же
 * собирается под конкретный планшет и должен работать сразу, поэтому
 * первый выбор аватара PIN не требует. За PIN осталась смена адреса
 * сервера и планшета — кнопка настроек.
 */
function goAfterConfigSuccess(navigate: NavigateFunction, language: AppLanguage): void {
  const appState = appStateStore.getState().appState;
  const person = appState.person;
  if (person === null) {
    PersonsRoute.go(navigate);
  } else {
    // `language.toString()` в Dart отдавал `param`; здесь значение языка —
    // сам `param`, так что адрес страницы получается прежний.
    ChatRoute.go(navigate, { lang: language, personId: person.id });
  }
}

/**
 * `_onLanguageSelect`.
 *
 * Внешнего `if (context.mounted)` нет: обработчик нажатия не может
 * начаться на странице, которой уже нет.
 */
async function selectLanguage(
  language: AppLanguage,
  navigate: NavigateFunction,
  isMounted: () => boolean,
): Promise<void> {
  try {
    EasyLoading.dismiss();
    EasyLoading.show();
    await languageStore.getState().setLanguage(language);
    const result = await appStateStore.getState().checkAndSetBaseUrl();
    // `if (mounted)` сторожил именно переход: уводить со страницы, которой
    // уже нет, нельзя.
    //
    // Ветки неудачи здесь больше нет. В Dart сбой мог увести посетителя на
    // экран адреса (когда адреса не было вовсе или сервер ответил 4xx) —
    // это был единственный способ переподключить планшет. Теперь планшет
    // вшит в сборку, а экран адреса — инструмент администратора за PIN,
    // поэтому посетитель остаётся на месте и видит причину в тосте;
    // повтор — это ещё один тап по языку, как и при 5xx раньше.
    if (isMounted() && result) {
      goAfterConfigSuccess(navigate, language);
    }
  } catch (e) {
    SnackbarManager.instance.showFailure(
      AppFailure.fromException(FailureOp.config, e, stackTraceOf(e)),
    );
  } finally {
    EasyLoading.dismiss();
  }
}

/**
 * `mounted` из `State`: у функционального компонента такого флага нет,
 * а он нужен — по нему решают, уводить ли со страницы.
 */
function useMountedRef(): RefObject<boolean> {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return mountedRef;
}

/**
 * `_versionTaps`, `_firstVersionTap` и `_onVersionTap`.
 *
 * Оба счётчика в отрисовке не участвуют, поэтому это `ref`, а не состояние —
 * в Dart они тоже были обычными полями `State`, без `setState`.
 */
function useVersionTaps(navigate: NavigateFunction): () => void {
  const versionTapsRef = useRef(0);
  /** Момент первого тапа текущего окна, в миллисекундах. */
  const firstVersionTapRef = useRef<number | null>(null);

  return useCallback((): void => {
    const now = Date.now();
    const first = firstVersionTapRef.current;
    if (first === null || now - first > VERSION_TAP_WINDOW_MS) {
      firstVersionTapRef.current = now;
      versionTapsRef.current = 0;
    }
    versionTapsRef.current += 1;
    if (versionTapsRef.current < VERSION_TAPS_TO_DIAGNOSTICS) return;
    versionTapsRef.current = 0;
    firstVersionTapRef.current = null;
    VerifyAdminRoute.go(navigate, VerifyAdminType.diagnostics);
  }, [navigate]);
}

/**
 * `_openSettings`.
 *
 * В Dart за этой кнопкой была смена аватара: список аватаров лежал за PIN,
 * и дверь вела к нему. Здесь список — вход в киоск и открыт всем, а сменить
 * собеседника можно стрелкой рядом. За PIN осталось то, что посетителю и
 * правда не положено, — адрес сервера и планшета.
 *
 * Ветка без адреса сохранена из оригинала, и она не дыра в защите, а
 * единственный вход. Без адреса запрос кода уходит в никуда
 * (`PathInterceptor` оставляет `${baseUrl}` в пути как есть), сверяться
 * остаётся только с сохранённым хешем, а на свежем киоске его ещё нет —
 * спросив код, мы заперли бы киоск навсегда.
 */
function useOpenSettings(navigate: NavigateFunction): () => void {
  return useCallback((): void => {
    const appState = appStateStore.getState().appState;
    const baseUrl = appState.baseUrl;
    if (baseUrl === null) {
      BaseUrlRoute.go(navigate);
      return;
    }
    VerifyAdminRoute.go(navigate, VerifyAdminType.changeServer);
  }, [navigate]);
}

export interface LanguageGridProps {
  readonly onSelect: (language: AppLanguage) => void;
}

/** `SliverGrid.builder` по `AppLanguage.values`. */
export function LanguageGrid({ onSelect }: LanguageGridProps): ReactElement {
  return (
    <div className={styles.grid}>
      {APP_LANGUAGE_VALUES.map((language) => {
        return (
          <PrimaryButton
            key={language}
            title={APP_LANGUAGE_NAME[language]}
            height={wv(GRID_ITEM_EXTENT)}
            onPressed={() => onSelect(language)}
          />
        );
      })}
    </div>
  );
}

export interface LanguageContentProps {
  readonly onSelect: (language: AppLanguage) => void;
}

/** `Center(SizedBox(1007.w, CustomScrollView(SliverPadding(MultiSliver))))`. */
export function LanguageContent({ onSelect }: LanguageContentProps): ReactElement {
  return (
    <div className={styles.column}>
      <div className={styles.padding}>
        <div className={styles.lottieRow}>
          <div className={styles.lottiePadding}>
            <LanguageLottie />
          </div>
        </div>
        <div className={styles.gap} />
        <LanguageGrid onSelect={onSelect} />
      </div>
    </div>
  );
}

/**
 * `Consumer` вокруг кнопки настроения аватара.
 *
 * На ноде без настроения (AVTR-1 Studio отвечает на `/v1/animate/mood`
 * 404) кнопки нет: она ничего бы не делала. Подвал раскладывает кнопки
 * через `gap`, так что пустого места на её месте не остаётся.
 */
export function MoodButton(): ReactElement | null {
  const personMoodState = usePersonMood();
  const supported = usePersonMoodSupported();
  if (!supported) return null;
  const isSelected =
    valueOrNull(personMoodState)?.moodState === PersonMoodState.bored;
  return (
    <PrimaryIconButtonRounded
      icon="personLock"
      isSelected={isSelected}
      onPressed={() => {
        void personMoodStore.getState().toggleMood();
      }}
    />
  );
}

export interface VersionLabelProps {
  readonly onTap: () => void;
}

/**
 * `Consumer` вокруг версии приложения.
 *
 * Это `GestureDetector`, а не кнопка: вход скрытый, и объявлять его
 * клавиатуре и скринридеру не нужно — в Dart семантики кнопки тоже не было.
 * `HitTestBehavior.opaque` отдельного аналога не требует: у блока с полями
 * нажатие в вебе ловится по всему прямоугольнику.
 */
export function VersionLabel({ onTap }: VersionLabelProps): ReactElement {
  const version = useAppStateStore((store) => {
    return formatVersion(store.appState.packageInfo.version.appVersion);
  });
  // 5 быстрых тапов по версии — журнал для разработчика (через
  // PIN). Посетитель этого входа не видит.
  return (
    <div className={styles.version} onClick={onTap}>
      {version}
    </div>
  );
}

export interface PersonNameLabelProps {
  readonly name: string;
}

/** `Positioned(left: 0, right: 0, child: Center(IgnorePointer(Opacity(0.5))))`. */
export function PersonNameLabel({ name }: PersonNameLabelProps): ReactElement {
  return (
    <div className={styles.personName}>
      <span className={styles.personNameText}>{name}</span>
    </div>
  );
}

interface FooterActionsOptions {
  readonly onBack: () => void;
  readonly onOpenSettings: () => void;
  readonly onVersionTap: () => void;
}

/**
 * `footerActions`.
 *
 * Добавка к Dart: кнопка «назад» к списку аватаров. В оригинале экран языка
 * был входом, возвращаться с него было некуда, и кнопки не было. Здесь вход —
 * список аватаров, а язык идёт после него, поэтому собеседника нужно уметь
 * сменить, не проходя через PIN. Стрелка стоит первой в подвале — так же,
 * как на экранах PIN и журнала.
 */
function buildFooterActions(options: FooterActionsOptions): ReactNode[] {
  return [
    <PrimaryIconButtonRounded icon="arrowLeft" onPressed={options.onBack} />,
    <PrimaryIconButtonRounded icon="settings" onPressed={options.onOpenSettings} />,
    <MoodButton />,
    <VersionLabel onTap={options.onVersionTap} />,
    <div style={SPACER_STYLE} />,
    <SvgAsset icon="neovexPoweredLogo" />,
  ];
}

/**
 * Экран выбора языка.
 *
 * Dart оборачивал всё в `Visibility.maintain(visible:
 * ModalRoute.isCurrentOf(context))`: в go_router остальные экраны вложены в
 * маршрут языка, поэтому при `go()` к соседнему (адрес → PIN, PIN → аватары)
 * этот оставался смонтированным под ним и просвечивал, пока новый
 * проявлялся. React Router рисует только совпавший маршрут — под дочерним
 * экраном этой страницы уже нет, скрывать нечего, и обёртка снята.
 */
export function LanguagePage(): ReactElement {
  const navigate = useNavigate();
  const personName = useAppStateStore((store) => store.appState.person?.name ?? null);
  const mountedRef = useMountedRef();
  const onVersionTap = useVersionTaps(navigate);
  const onOpenSettings = useOpenSettings(navigate);
  const onBack = useCallback((): void => {
    PersonsRoute.go(navigate);
  }, [navigate]);

  const onSelect = useCallback(
    (language: AppLanguage): void => {
      void selectLanguage(language, navigate, () => mountedRef.current);
    },
    // `mountedRef` приходит из своего хука и для линтера не «стабилен»,
    // хотя ссылка на ref не меняется никогда — указан ради правила.
    [navigate, mountedRef],
  );

  return (
    <BackgroundWrapper
      footerStackedActions={
        personName === null ? [] : [<PersonNameLabel name={personName} />]
      }
      footerActions={buildFooterActions({ onBack, onOpenSettings, onVersionTap })}
    >
      <LanguageContent onSelect={onSelect} />
    </BackgroundWrapper>
  );
}
